import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLanguage } from "@/i18n/index";
import PatientLayout from "@/components/layout/PatientLayout";
import api from "@/api";
import { toast } from "sonner";
import {
  ArrowLeft, BookOpen, Play, Clock, CheckCircle,
  Heart, Utensils, AlertTriangle, Smile, Sparkles,
  Stethoscope, Activity
} from "lucide-react";

// Category configuration (icons/colors only — labels/descriptions come from i18n)
const CATEGORY_CONFIG = {
  BIENVENUE: { icon: Sparkles, color: "emerald" },
  TRAITEMENT: { icon: Stethoscope, color: "blue" },
  NUTRITION: { icon: Utensils, color: "orange" },
  EFFETS_SECONDAIRES: { icon: Activity, color: "purple" },
  SIGNES_ALERTE: { icon: AlertTriangle, color: "red" },
  BIEN_VIVRE: { icon: Smile, color: "teal" },
  GESTION_SYMPTOMES: { icon: Heart, color: "pink" },
};

const getCategoryConfig = (category) => {
  return CATEGORY_CONFIG[category] || { icon: BookOpen, color: "slate" };
};

export default function EducationCategory() {
  const { category } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [contents, setContents] = useState([]);

  const config = getCategoryConfig(category);
  const Icon = config.icon;

  useEffect(() => {
    fetchCategoryContents();
  }, [category]);

  const fetchCategoryContents = async () => {
    try {
      const res = await api.get(`/education/patient/category/${category}`);
      setContents(res.data);
    } catch (err) {
      // error handled by toast
      toast.error(t("education.loadContentsError"));
    } finally {
      setLoading(false);
    }
  };

  const handleContentClick = (content) => {
    navigate(`/patient/education/${content.id}`);
  };

  if (loading) {
    return (
      <PatientLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </PatientLayout>
    );
  }

  const colorClasses = {
    emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", icon: "text-emerald-600" },
    blue: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", icon: "text-blue-600" },
    orange: { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-700", icon: "text-orange-600" },
    purple: { bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-700", icon: "text-purple-600" },
    red: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", icon: "text-red-600" },
    teal: { bg: "bg-teal-50", border: "border-teal-200", text: "text-teal-700", icon: "text-teal-600" },
    pink: { bg: "bg-pink-50", border: "border-pink-200", text: "text-pink-700", icon: "text-pink-600" },
    slate: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-700", icon: "text-slate-600" },
  };

  const colors = colorClasses[config.color];

  return (
    <PatientLayout>
      <div className="space-y-5 pb-8">
        {/* Back button */}
        <button
          onClick={() => navigate("/patient/education")}
          data-testid="education-back-btn"
          className="flex items-center gap-2 text-slate-600 hover:text-emerald-600 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">{t("education.backToEducation")}</span>
        </button>

        {/* Category header */}
        <div className={`${colors.bg} ${colors.border} border-2 rounded-2xl p-5`}>
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-2xl ${colors.bg} border-2 ${colors.border} flex items-center justify-center`}>
              <Icon className={`w-8 h-8 ${colors.icon}`} />
            </div>
            <div>
              <h1 className={`text-xl font-bold ${colors.text}`}>{t(`education.categories.${category}`)}</h1>
              <p className="text-slate-600 mt-1">{t(`education.categoryDescriptions.${category}`)}</p>
              <p className="text-sm text-slate-500 mt-1">{contents.length} {t("education.contents")}</p>
            </div>
          </div>
        </div>

        {/* Content list */}
        {contents.length > 0 ? (
          <div className="grid gap-4">
            {contents.map((content) => {
              const isVideo = content.contentType === "VIDEO";
              const duration = content.durationSeconds 
                ? `${Math.round(content.durationSeconds / 60)} min`
                : null;

              return (
                <button
                  key={content.id}
                  onClick={() => handleContentClick(content)}
                  data-testid={`education-content-${content.id}`}
                  className="w-full bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg hover:border-emerald-200 transition-all text-left"
                >
                  <div className="flex">
                    {/* Thumbnail */}
                    {isVideo && content.thumbnailUrl ? (
                      <div className="relative w-32 h-24 flex-shrink-0">
                        <img
                          src={content.thumbnailUrl}
                          alt={content.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center">
                            <Play className="w-5 h-5 text-emerald-600 ml-0.5" fill="currentColor" />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className={`w-32 h-24 flex-shrink-0 ${colors.bg} flex items-center justify-center`}>
                        <Icon className={`w-10 h-10 ${colors.icon} opacity-50`} />
                      </div>
                    )}

                    {/* Content info */}
                    <div className="flex-1 p-4">
                      <div className="flex items-center gap-2 mb-1">
                        {content.viewed && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-600 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            {t("education.viewed")}
                          </span>
                        )}
                        {duration && (
                          <span className="px-2 py-0.5 text-xs text-slate-500 bg-slate-100 rounded-full flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {duration}
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-slate-800 line-clamp-1">{content.title}</h3>
                      <p className="text-sm text-slate-500 mt-1 line-clamp-2">{content.shortDescription}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-600 mb-2">
              {t("education.noContent")}
            </h3>
            <p className="text-slate-500">
              {t("education.categoryContentsWillAppear")}
            </p>
          </div>
        )}
      </div>
    </PatientLayout>
  );
}
